/** Rich-text email body for every mailbox (Emma, 9 Oct 2026): bold / italic / underline / bullets / numbering / links.
 *  Emits both HTML (what Outlook sends) and plain text (what we log on files). Ctrl+B / I / U work as in Outlook. */
import { useEffect, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { DOMParser as PMDOMParser } from "@tiptap/pm/model";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { Bold, ImagePlus, Italic, Link2, List, ListOrdered, RemoveFormatting, Underline } from "lucide-react";

const MAX_IMAGE = 8 * 1024 * 1024;
const imageFiles = (list) => Array.from(list || []).filter((f) => f && f.type && f.type.startsWith("image/"));
const readDataUrl = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
/** Pasted / dropped screenshots go straight into the body (John Garcia, 28 Oct 2026) — sent later as inline cid images. */
async function insertImages(editor, files, at) {
  for (const f of files) {
    if (f.size > MAX_IMAGE) { window.alert(`${f.name || "That image"} is over 8 MB — attach it as a file instead.`); continue; }
    const src = await readDataUrl(f);
    const nodes = [{ type: "image", attrs: { src, alt: f.name || "screenshot" } }, { type: "paragraph" }];  // caret lands in the paragraph after the image, so typing doesn't replace it
    const chain = editor.chain().focus();
    (at != null ? chain.insertContentAt(at, nodes) : chain.insertContent(nodes)).run();
  }
}

/** Purely visual highlight of every match of a regex (e.g. [placeholders] in a witness statement) — a ProseMirror decoration,
 *  so it follows the text as it is edited and vanishes when the brackets go, without touching the HTML that is saved. */
const patternHighlight = (pattern, className) => Extension.create({
  name: "patternHighlight",
  addProseMirrorPlugins() {
    const flags = pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`;
    const build = (doc) => {
      const decos = [];
      doc.descendants((node, pos) => {
        if (!node.isText) return;
        const rx = new RegExp(pattern.source, flags);
        let m;
        while ((m = rx.exec(node.text)) !== null) decos.push(Decoration.inline(pos + m.index, pos + m.index + m[0].length, { class: className }));
      });
      return DecorationSet.create(doc, decos);
    };
    return [new Plugin({
      key: new PluginKey("patternHighlight"),
      state: { init: (_, { doc }) => build(doc), apply: (tr, old) => (tr.docChanged ? build(tr.doc) : old) },
      props: { decorations(state) { return this.getState(state); } },
    })];
  },
});

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
/** Plain text (AI drafts, prefills, templates) → paragraphs the editor understands. */
export const textToEditorHtml = (text) => (text || "").replace(/\r\n/g, "\n").split("\n").map((l) => (l.trim() ? `<p>${esc(l)}</p>` : "<p></p>")).join("");

/** Pasted plain text keeps EVERY blank line as an empty paragraph (ProseMirror's default collapses "\n\n" into a bare
 *  paragraph break, so pasted emails lost their spacing — Juffrey, 12 Oct 2026). */
const pasteTextKeepingBlankLines = (text, $context, _plain, view) => {
  const dom = document.createElement("div");
  dom.innerHTML = textToEditorHtml(text);
  return PMDOMParser.fromSchema(view.state.schema).parseSlice(dom, { preserveWhitespace: true, context: $context });
};

function ToolButton({ on, active, title, children, testId }) {
  return (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); on(); }} title={title} data-testid={testId} aria-pressed={!!active}
      className={`h-7 w-7 inline-flex items-center justify-center rounded text-slate-600 hover:bg-slate-200/70 ${active ? "bg-slate-200 text-slate-900" : ""}`}>
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, placeholder, minHeight = 240, autoFocus = false, testId = "rich-editor", disabled = false, highlightPattern = null, highlightClass = "rte-highlight", images = false }) {
  const imgRef = useRef(null);
  const editorRef = useRef(null);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: false, codeBlock: false, code: false, blockquote: false, horizontalRule: false, link: { openOnClick: false, autolink: true, defaultProtocol: "https" } }),
      ...(images ? [Image.configure({ inline: false, allowBase64: true })] : []),
      ...(highlightPattern ? [patternHighlight(highlightPattern, highlightClass)] : [])],
    content: value || "",
    autofocus: autoFocus ? "end" : false,
    editable: !disabled,
    immediatelyRender: true,
    editorProps: {
      clipboardTextParser: pasteTextKeepingBlankLines,
      attributes: { class: `outline-none text-sm leading-relaxed px-3 py-2 prose-sm max-w-none [&_p]:my-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-blue-700 [&_a]:underline${images ? " [&_img]:max-w-full [&_img]:h-auto [&_img]:my-1.5 [&_img]:rounded [&_img]:border [&_img]:border-slate-200 [&_img.ProseMirror-selectednode]:ring-2 [&_img.ProseMirror-selectednode]:ring-blue-400" : ""}`, "data-testid": `${testId}-content`, "data-placeholder": placeholder || "" },
      ...(images ? {
        handlePaste: (view, event) => {
          const files = imageFiles(event.clipboardData?.files);
          if (!files.length) return false;
          event.preventDefault();
          insertImages(editorRef.current, files);
          return true;
        },
        handleDrop: (view, event) => {
          const files = imageFiles(event.dataTransfer?.files);
          if (!files.length) return false;
          event.preventDefault(); event.stopPropagation();
          const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
          insertImages(editorRef.current, files, pos);
          return true;
        },
      } : {}),
    },
    onUpdate: ({ editor: ed }) => onChange({ html: ed.isEmpty ? "" : ed.getHTML(), text: ed.getText({ blockSeparator: "\n" }) }),
  });
  editorRef.current = editor;

  // external replace (an AI draft arriving, a template inserted) — only when it differs from what the editor already
  // holds; emits onChange so the caller's plain-text copy follows the new content
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML() && !(value === "" && editor.isEmpty)) editor.commands.setContent(value || "", { emitUpdate: true });
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link address", prev || "https://");
    if (url === null) return;
    if (!url.trim()) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };
  const empty = editor.isEmpty;
  return (
    <div className={`rounded-md border border-input bg-white focus-within:ring-1 focus-within:ring-ring ${disabled ? "opacity-60" : ""}`} data-testid={testId}>
      <div className="flex items-center gap-0.5 border-b bg-slate-50/80 px-1.5 py-1" data-testid={`${testId}-toolbar`}>
        <ToolButton on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Ctrl+B)" testId={`${testId}-bold`}><Bold className="w-3.5 h-3.5" /></ToolButton>
        <ToolButton on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Ctrl+I)" testId={`${testId}-italic`}><Italic className="w-3.5 h-3.5" /></ToolButton>
        <ToolButton on={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline (Ctrl+U)" testId={`${testId}-underline`}><Underline className="w-3.5 h-3.5" /></ToolButton>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolButton on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId={`${testId}-bullets`}><List className="w-3.5 h-3.5" /></ToolButton>
        <ToolButton on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testId={`${testId}-numbers`}><ListOrdered className="w-3.5 h-3.5" /></ToolButton>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolButton on={setLink} active={editor.isActive("link")} title="Insert link" testId={`${testId}-link`}><Link2 className="w-3.5 h-3.5" /></ToolButton>
        {images && (
          <>
            <ToolButton on={() => imgRef.current?.click()} title="Insert a screenshot / image — or just paste it (Ctrl+V)" testId={`${testId}-image`}><ImagePlus className="w-3.5 h-3.5" /></ToolButton>
            <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" data-testid={`${testId}-image-input`}
              onChange={(e) => { insertImages(editor, imageFiles(e.target.files)); e.target.value = ""; }} />
          </>
        )}
        <ToolButton on={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" testId={`${testId}-clear`}><RemoveFormatting className="w-3.5 h-3.5" /></ToolButton>
        {images && <span className="ml-auto text-[10px] text-slate-400 pr-1 hidden sm:inline" data-testid={`${testId}-paste-hint`}>Paste screenshots straight into the reply</span>}
      </div>
      <div className="relative" style={{ minHeight }}>
        {empty && placeholder && <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground" data-testid={`${testId}-placeholder`}>{placeholder}</div>}
        <EditorContent editor={editor} style={{ minHeight }} />
      </div>
    </div>
  );
}
